import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash, DotsThreeVertical, PencilSimple, Eye, Copy, XCircle, ArrowUUpLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { sku_code: "", name: "", qty: 0, rate: 0, amount: 0 };

// ============== SO Editor ==============
function SOEditor({ open, mode, initial, fgSkus, onClose, onDone }) {
  const [customer, setCustomer] = useState("");
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [items, setItems] = useState([EMPTY]);
  const readOnly = mode === "view";

  useEffect(() => {
    if (open) {
      setCustomer(initial?.customer || "");
      setCourier(initial?.courier || "");
      setTracking(initial?.tracking_no || "");
      setItems(initial?.items?.length ? initial.items : [{...EMPTY}]);
    }
  }, [open, initial]);

  const upd = (i, f, v) => {
    const c = [...items]; c[i] = {...c[i], [f]: v};
    c[i].amount = (parseFloat(c[i].qty) || 0) * (parseFloat(c[i].rate) || 0);
    setItems(c);
  };
  const total = items.reduce((a, x) => a + (x.amount || 0), 0);

  const submit = async () => {
    try {
      const body = { customer, courier, tracking_no: tracking, items };
      if (mode === "edit") {
        await api.put(`/sales-orders/${initial.id}`, body);
        toast.success("Order updated");
      } else if (mode === "duplicate") {
        await api.post(`/sales-orders/${initial.id}/duplicate`);
        toast.success("Order duplicated");
      } else {
        await api.post("/sales-orders", body);
        toast.success("Sales order created");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const title = { edit: "Edit Sales Order", view: "Sales Order Details",
                    duplicate: `Duplicate ${initial?.order_no || ""}`, create: "Create Sales Order" }[mode] || "Sales Order";

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>

        {mode === "duplicate" ? (
          <div className="py-4">
            <p className="text-sm text-muted-foreground">Create a new draft order from <b>{initial?.order_no}</b> with the same customer, courier and line items.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer</Label><Input value={customer} onChange={e => setCustomer(e.target.value)} disabled={readOnly} data-testid="so-customer"/></div>
              <div><Label>Courier</Label><Input value={courier} onChange={e => setCourier(e.target.value)} disabled={readOnly}/></div>
              <div className="col-span-2"><Label>Tracking No (optional)</Label><Input value={tracking} onChange={e => setTracking(e.target.value)} disabled={readOnly}/></div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between mb-2">
                <Label>Items</Label>
                {!readOnly && <Button size="sm" variant="outline" onClick={() => setItems([...items, {...EMPTY}])}><Plus size={14}/></Button>}
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-5">
                    <Select value={it.sku_code} onValueChange={v => {
                      const s = fgSkus.find(f => f.sku_code === v);
                      upd(i, "sku_code", v); if (s) upd(i, "name", s.sku_name);
                    }} disabled={readOnly}>
                      <SelectTrigger><SelectValue placeholder="SKU"/></SelectTrigger>
                      <SelectContent>{fgSkus.map(f => <SelectItem key={f.sku_code} value={f.sku_code}>{f.sku_code} · {f.sku_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.qty} onChange={e => upd(i, "qty", e.target.value)} disabled={readOnly}/></div>
                  <div className="col-span-2"><Input type="number" placeholder="Rate" value={it.rate} onChange={e => upd(i, "rate", e.target.value)} disabled={readOnly}/></div>
                  <div className="col-span-2 text-right mono text-sm">₹{(it.amount || 0).toFixed(2)}</div>
                  {!readOnly && <button className="col-span-1 text-red-600" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash size={14}/></button>}
                </div>
              ))}
              <div className="border-t border-border mt-3 pt-3 flex justify-between font-semibold">
                <span>Total</span><span className="mono">₹{total.toFixed(2)}</span>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          {readOnly ? <Button variant="outline" onClick={onClose}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={mode !== "duplicate" && !customer} data-testid="so-save">
                {mode === "edit" ? "Save Changes" : mode === "duplicate" ? "Duplicate" : "Create"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelSoDialog({ open, so, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    try {
      await api.post(`/sales-orders/${so.id}/cancel`, { reason });
      toast.success("Order cancelled");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Cancel Order {so?.order_no}</DialogTitle><DialogDescription className="sr-only">Cancel order</DialogDescription></DialogHeader>
        <div className="py-2">
          <p className="text-sm text-muted-foreground mb-3">The order will be marked <b>Cancelled</b> and preserved for audit.</p>
          <Label>Reason</Label>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Customer cancelled, out of stock, duplicate order…" data-testid="so-cancel-reason"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep Order</Button>
          <Button variant="destructive" onClick={submit} data-testid="so-cancel-confirm">Confirm Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dispatch() {
  const { user } = useAuth();
  const canManage = ["admin", "sales", "dispatch"].includes(user?.role);

  const [orders, setOrders] = useState([]);
  const [fg, setFg] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const [soMode, setSoMode] = useState(null);
  const [soSel, setSoSel] = useState(null);
  const [cancelSo, setCancelSo] = useState(null);

  const load = async () => {
    setOrders((await api.get("/sales-orders", { params: { include_deleted: showDeleted } })).data);
    setFg((await api.get("/finished-goods")).data);
  };
  useEffect(() => { load(); }, [showDeleted]);  // eslint-disable-line

  const fgSkus = Array.from(new Map(fg.map(f => [f.sku_code, f])).values());

  const setStatus = async (id, status) => {
    try { await api.post(`/sales-orders/${id}/status`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restore = async (o) => {
    try { await api.post(`/sales-orders/${o.id}/restore`); toast.success("Order restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete draft order ${o.order_no}? It will be marked as cancelled and archived.`)) return;
    try { await api.delete(`/sales-orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="dispatch-page">
      <PageHeader subtitle="Orders & Shipping" title="Dispatch"
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} data-testid="so-show-deleted"/>
              Show archived
            </label>
            {canManage && (
              <Button onClick={() => { setSoSel(null); setSoMode("create"); }} data-testid="new-so-btn">
                <Plus size={16} className="mr-1.5"/>New Sales Order
              </Button>
            )}
          </div>
        }
      />

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead><TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total ₹</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Courier</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map(o => {
                const next = {"Pending": "Picked", "Picked": "Packed", "Packed": "Dispatched", "Dispatched": "Delivered"}[o.status];
                const isCancelled = o.status === "Cancelled";
                const isDone = ["Delivered", "Dispatched"].includes(o.status);
                const isDeleted = !!o.deleted_at;
                return (
                  <TableRow key={o.id} className={isCancelled || isDeleted ? "opacity-60" : ""} data-testid={`so-row-${o.order_no}`}>
                    <TableCell className="mono font-semibold">{o.order_no}</TableCell>
                    <TableCell>{o.customer}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.items?.length || 0} lines</TableCell>
                    <TableCell className="text-right mono">{o.total?.toLocaleString("en-IN")}</TableCell>
                    <TableCell><StatusBadge status={o.status}/></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.courier || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        {next && !isCancelled && canManage && (
                          <Button size="sm" variant="outline" onClick={() => setStatus(o.id, next)} data-testid={`so-next-${o.order_no}`}>{next}</Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`so-actions-${o.order_no}`}>
                              <DotsThreeVertical size={16} weight="bold"/>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => { setSoSel(o); setSoMode("view"); }} data-testid={`so-view-${o.order_no}`}>
                              <Eye size={14} className="mr-2"/>View
                            </DropdownMenuItem>
                            {canManage && !isDone && !isCancelled && (
                              <DropdownMenuItem onSelect={() => { setSoSel(o); setSoMode("edit"); }} data-testid={`so-edit-${o.order_no}`}>
                                <PencilSimple size={14} className="mr-2"/>Edit
                              </DropdownMenuItem>
                            )}
                            {canManage && (
                              <DropdownMenuItem onSelect={() => { setSoSel(o); setSoMode("duplicate"); }} data-testid={`so-dup-${o.order_no}`}>
                                <Copy size={14} className="mr-2"/>Duplicate
                              </DropdownMenuItem>
                            )}
                            {canManage && !isCancelled && !isDone && (
                              <>
                                <DropdownMenuSeparator/>
                                <DropdownMenuItem className="text-red-600" onSelect={() => setCancelSo(o)} data-testid={`so-cancel-${o.order_no}`}>
                                  <XCircle size={14} className="mr-2"/>Cancel Order
                                </DropdownMenuItem>
                                {o.status === "Pending" && (
                                  <DropdownMenuItem className="text-red-600" onSelect={() => del(o)} data-testid={`so-delete-${o.order_no}`}>
                                    <Trash size={14} className="mr-2"/>Delete Draft
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {canManage && (isCancelled || isDeleted) && (
                              <DropdownMenuItem className="text-emerald-700" onSelect={() => restore(o)} data-testid={`so-restore-${o.order_no}`}>
                                <ArrowUUpLeft size={14} className="mr-2"/>Restore
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SOEditor open={!!soMode} mode={soMode} initial={soSel} fgSkus={fgSkus}
                 onClose={() => { setSoMode(null); setSoSel(null); }} onDone={load}/>
      <CancelSoDialog open={!!cancelSo} so={cancelSo} onClose={() => setCancelSo(null)} onDone={load}/>
    </div>
  );
}
